import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Contact, Mail, Phone } from 'lucide-react';
import { Card } from '@shared/ui/containers/Card';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { useExperienceStore } from '@shared/stores/experienceStore';
import type { CompiledMemberAppExperience, CompiledPublicTile } from '@shared/types/domainConfig';
import { fetchConfiguredContactGroups, type ContactGroupsContact } from './api';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function findTile(experience: CompiledMemberAppExperience | null, tileId: string | null): CompiledPublicTile | null {
  if (!experience || !tileId) {
    return null;
  }

  const tiles = [
    ...experience.tiles.main,
    ...experience.tiles.more_categories.flatMap((category) => category.tiles),
  ];
  return tiles.find((tile) => tile.id === tileId) ?? null;
}

/**
 * Contact groups — category chips over contact cards, mirroring the Suppliers /
 * Directory ("Who to call") UX. One group is active at a time (defaults to the
 * first); tapping a chip swaps the visible group.
 */
export function ContactGroups(): JSX.Element {
  const { search } = useLocation();
  const experience = useExperienceStore((state) => state.experience);
  const tileId = useMemo(() => new URLSearchParams(search).get('tile'), [search]);
  const tile = useMemo(() => findTile(experience, tileId), [experience, tileId]);
  const configuredGroupIds = useMemo(
    () => [...new Set((tile?.action_config.contact_group_ids ?? []).map((id) => id.trim()).filter(Boolean))],
    [tile],
  );

  const [activeId, setActiveId] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['contact-groups-tile', tile?.id, configuredGroupIds],
    queryFn: () => fetchConfiguredContactGroups(configuredGroupIds),
    enabled: configuredGroupIds.length > 0,
  });

  const groups = query.data ?? [];
  const active = useMemo(
    () => groups.find((group) => group.id === activeId) ?? groups[0],
    [groups, activeId],
  );
  const visibleContacts = useMemo(
    () => (active ? active.contacts.map((contact) => ({ contact, categoryName: active.name })) : []),
    [active],
  );

  const title = tile?.label ?? 'Contact Groups';
  const subtitle = tile?.heading ?? 'Useful contacts published by your community.';

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader eyebrow="Contact Groups" title={title} subtitle={subtitle} />

      {/* Category chips */}
      {groups.length > 0 ? (
        <div className="flex gap-2 overflow-x-auto px-5 pt-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {groups.map((group) => {
            const on = (active?.id ?? groups[0]?.id) === group.id;
            return (
              <button
                key={group.id}
                type="button"
                onClick={() => setActiveId(group.id)}
                className={
                  'flex shrink-0 items-center gap-1.5 rounded-pill px-4 py-2 text-body-sm font-medium transition-colors ' +
                  (on ? 'bg-primary-700 text-white' : 'bg-card hairline text-ink shadow-card')
                }
              >
                <Contact
                  className={'h-4 w-4 ' + (on ? 'text-white' : 'text-ink-muted')}
                  strokeWidth={1.75}
                />
                {group.name}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Contacts */}
      <div className="flex flex-col gap-3 px-5 pt-4 pb-safe-or-6">
        {query.isLoading ? (
          <>
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </>
        ) : query.isError ? (
          <ErrorState onRetry={() => query.refetch()} />
        ) : configuredGroupIds.length === 0 || visibleContacts.length === 0 ? (
          <EmptyState
            icon={<Contact className="h-7 w-7" strokeWidth={1.5} />}
            title="No contacts."
            description="No contact groups have been linked to this tile yet."
          />
        ) : (
          visibleContacts.map(({ contact, categoryName }) => (
            <ContactCard
              key={`${categoryName}-${contact.id}`}
              contact={contact}
              categoryName={categoryName}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ContactCard({
  contact,
  categoryName,
}: {
  contact: ContactGroupsContact;
  categoryName: string;
}): JSX.Element {
  return (
    <Card asChild noGrain className="rounded-2xl p-4">
      <article>
        <div className="flex items-start gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-pill bg-primary-50">
            {contact.profile_image_url ? (
              <img src={contact.profile_image_url} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="text-body-sm font-semibold text-primary-700">
                {initials(contact.name)}
              </span>
            )}
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-headline leading-tight text-ink">{contact.name}</p>
            {contact.role ? (
              <p className="text-body-sm text-ink-secondary">{contact.role}</p>
            ) : null}
            <span className="mt-1 inline-flex max-w-full items-center rounded-pill bg-primary-50 px-2 py-0.5 text-[11px] font-semibold leading-4 text-primary-700">
              <span className="truncate">{categoryName}</span>
            </span>
          </div>
        </div>

        <div className="mt-4 grid gap-2">
          {contact.mobile ? (
            <a href={`tel:${contact.mobile}`} className="flex items-center gap-2 rounded-xl bg-paper px-3 py-2 text-body-sm text-ink">
              <Phone className="h-4 w-4 text-primary-700" strokeWidth={1.75} />
              <span>{contact.mobile}</span>
            </a>
          ) : null}
          {contact.email ? (
            <a href={`mailto:${contact.email}`} className="flex items-center gap-2 rounded-xl bg-paper px-3 py-2 text-body-sm text-ink">
              <Mail className="h-4 w-4 text-primary-700" strokeWidth={1.75} />
              <span className="min-w-0 truncate">{contact.email}</span>
            </a>
          ) : null}
        </div>
      </article>
    </Card>
  );
}
